import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { FileDown, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SupplierOrderDialog = ({ order, onClose }) => {
  const [busy, setBusy] = useState('');
  const po = order?.po_number || order?.order_name || order?.order_id;

  const getPdf = async () => {
    const res = await axios.get(`${API}/suppliers/me/orders/${order.order_id}/pdf`, { withCredentials: true, responseType: 'blob' });
    return URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  };

  const download = async () => {
    setBusy('pdf');
    try {
      const url = await getPdf();
      const a = document.createElement('a');
      a.href = url; a.download = `PO_${po}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error('Failed to create PDF'); }
    finally { setBusy(''); }
  };

  const print = async () => {
    setBusy('print');
    const win = window.open('', '_blank');
    try {
      const url = await getPdf();
      if (win) win.location.href = url; else window.location.href = url;
    } catch { toast.error('Failed to open PDF'); win?.close(); }
    finally { setBusy(''); }
  };

  const items = (order?.items || []).filter((i) => (i.order_qty || 0) > 0);

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="supplier-order-dialog">
        <DialogHeader>
          <DialogTitle>Purchase Order {po}</DialogTitle>
          <DialogDescription>
            {order?.branch_name} · {order?.date ? new Date(order.date).toLocaleDateString() : ''}{order?.created_by ? ` · Confirmed by ${order.created_by}` : ''}
          </DialogDescription>
        </DialogHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Order</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i, idx) => (
              <TableRow key={idx} data-testid={`supplier-order-item-${idx}`}>
                <TableCell>{i.item_name}</TableCell>
                <TableCell>{i.unit}</TableCell>
                <TableCell className="text-right font-semibold">{i.order_qty}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {order?.notes && <p className="text-sm"><span className="text-muted-foreground">Notes:</span> {order.notes}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={download} disabled={!!busy} data-testid="supplier-order-pdf-btn">
            <FileDown className="w-4 h-4 mr-2" />{busy === 'pdf' ? 'Creating...' : 'Download PDF'}
          </Button>
          <Button onClick={print} disabled={!!busy} data-testid="supplier-order-print-btn">
            <Printer className="w-4 h-4 mr-2" />{busy === 'print' ? 'Opening...' : 'Print'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
